import React, { useState, useCallback, useMemo } from "react";
import AdminHOC from "../../../components/shared/HOC/AdminHOC";
import CustomTab from "../../../components/shared/tab/TabComponent";
import CustomInput from "../../../components/global/CustomInput/CustomInput";
import CommonTable from "../../../components/shared/table/Table";
import { useAdminCourseData } from "../../../hooks/AdminHooks/useAdminCourseData";
import { getCourseTableColumns } from "../../../assets/constants/admin/courses";
import { useNavigate } from "react-router-dom";
import LottieIcon from "../../../components/shared/lottieIcon/LottieIcon";
import searchIconsvg   from "../../../assets/icons/Search-Icon.svg";
import CustomButton from "../../../components/global/Button/CustomButton";
import CustomModal from "../../../components/global/CustomModal/CustomModal";
import Form from "../../../../src/components/shared/new Form/Fields/Form";
import { createCourse, updateCourse, deleteCourse } from "../../../service/AdminService/BookService";
import { courseFields } from "../../../assets/FormFeilds/AddCourseFields";
import { message } from "antd";
import { courseSectionConfig } from "../../../assets/FormFeilds/Config/formFieldConfig";
import { jwtDecode } from "jwt-decode";

const tabList = [
  { key: "active", label: "Active Courses" },
  { key: "inactive", label: "Inactive Courses" },
];

const UserCourses = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("authtoken");
  let currentUserId = null;
  if (token) {
    try {
      const decoded = jwtDecode(token);
      currentUserId = decoded.userId || decoded.sub || null;
    } catch (err) {
      console.error("Invalid token format", err);
    }
  }
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("active");
  const [isInputHovered, setIsInputHovered] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const adminData = useAdminCourseData(status, search);
  const { data, loading, paginationConfig, handlePageChange, refreshData } = adminData;

  const handleClose = useCallback(() => {
    setIsModalOpen(false);
    setFormData({});
    setFormErrors({});
  }, []);

  const handleEditClick = (record) => {
    setFormData({
      ...record,
      courseLevel: record.level || "BEGINNER",
      isActive: record.active ? "true" : "false",
      ownerId: currentUserId,
    });
    setIsModalOpen(true);
  };

  const handleOpenAddModal = () => {
    setFormData({ 
      ownerId: currentUserId, 
      status: true,
      courseLevel: "BEGINNER" 
    });
    setIsModalOpen(true);
  };

  const handleFormChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };
  const validate = () => {
    let errors = {};
    courseFields.forEach((field) => {
      if (field.required && !formData[field.name]) {
        errors[field.name] = `${field.label} is required`;
      }
    });
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormSubmit = async () => {
    if (!validate()) return;

    const payload = {
      title: formData.title,
      description: formData.description,
      ownerId: parseInt(formData.ownerId, 10),
      courseLevel: formData.courseLevel,
      isActive: formData.isActive === "true"
    };

    setIsSubmitting(true);
    try {
      if (formData.courseId) {
        await updateCourse(formData.courseId, payload);
        message.success("Course updated successfully");
      } else {
        await createCourse(payload);
        message.success("Course created successfully");
      }
      if (typeof refreshData === "function") {
        refreshData();
      }
      handleClose();
    } catch (error) {
      message.error(error?.response?.data?.message || error?.message || "Operation failed");
    } finally {
      setIsSubmitting(false);
    }
  };
   const handleDeleteCourse = async (courseId) => {
    try {
      const result = await deleteCourse(courseId);
      message.success(result?.message || "Course deleted successfully!");
      if (typeof refreshData === "function") {
        refreshData();
      }
    } catch (error) {
      message.error(error?.response?.data?.message || error?.message || "Course cannot be deleted.");
    }
  };

  const columns = useMemo(
    () => getCourseTableColumns(navigate, handleEditClick, handleDeleteCourse,status),
  [navigate,status, currentUserId]
  );

  const handleTabChange = (key) => {
    setStatus(key);
    setSearch("");
  };

  return (
    <div className="rounded-[10px] border p-4 border-[#e8e8e8] bg-white shadow-[0_0_6px_#0000000D] overflow-visible flex flex-col gap-6">
      <h1 className="text-[#e74860] text-[1.75rem] font-normal">Courses</h1>

      <div>
      <div className="flex justify-between">
        <CustomTab onChange={handleTabChange} entries={tabList} />
        <div className="flex gap-4">
          <CustomInput
            key={status}
            icon={
              isInputHovered ? (
                <LottieIcon name="searchIcon" size={24} shouldPlay={true} />
              ) : (
                <img src={searchIconsvg} alt="search" className="w-6 h-6" />
              )
            }
            style={{ width: 250 }}
            onChange={(e) => setSearch(e.target.value)}
            onMouseEnter={() => setIsInputHovered(true)}
            onMouseLeave={() => setIsInputHovered(false)}
            placeholder="Search by Name"
            allowClear
          />
          <CustomButton
            onClick={() => navigate(`/books/course-report?status=${status==="active" ? "active" : "inactive"}`)}
          >
            Report
          </CustomButton>
          <CustomButton
            onClick={handleOpenAddModal}
            disabled={status === "inactive"}
          >
            Add Course
          </CustomButton>
        </div>
      </div>
      
      <CommonTable
        data={data}
        scroll={{ y: "48vh"}}
        className="hide-scrollbar top-table-radius"
        columns={columns}
        showLoader={loading}
        pagination={paginationConfig}
        onChange={handlePageChange}
        serverSide={true}
        tableLayout="fixed" 
      />
      </div>

      <CustomModal
        visible={isModalOpen}
        onClose={handleClose}
        title={formData.courseId ? "Edit Course" : "Add New Course"}
        width={550}
      >
        <Form
          fields={courseFields}
          formData={formData}
          sectionConfig={courseSectionConfig}
          errors={formErrors}
          setErrors={setFormErrors}
          onChange={handleFormChange}
          onSubmit={handleFormSubmit}
          onCancel={handleClose}
          showClearButton={false}
          isSubmitting={isSubmitting}
          showCancelButton={true}
        />
      </CustomModal>
    </div>
  );
};

export default AdminHOC(UserCourses);